import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, Fragment, useMemo, useCallback, useState, useContext } from 'react';
import { NavMarker } from 'react-rhumb';
import I18n from 'I18n';
import getLang from 'I18n/utils/getLang';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { H1, H2, H3 } from 'UIComponents/elements/headings';
import HrefLangLink from 'marketplace-ui-common/components/elements/HrefLangLink';
import CanonicalLink from 'ecosystem-marketplace-core/components/elements/CanonicalLink'; // @ts-expect-error untyped dependency

import CoreStorefrontHeader from 'ecosystem-marketplace-core/components/storefront/StorefrontHeader';
import MainSection from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import MetaDescription from 'ecosystem-marketplace-core/components/elements/MetaDescription';
import MetaOpenGraph from 'ecosystem-marketplace-core/components/elements/MetaOpenGraph';
import MetaTwitter from 'ecosystem-marketplace-core/components/elements/MetaTwitter';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady';
import { getFullTemplatesPublicUrl } from '../../utils/urls';
import { PublicAppContext } from '../../selectors/app'; // @ts-expect-error untyped dependency

import { useEmbedContext } from '../../hooks/useEmbedContext';
import { useSocialScreenshotUrl } from '../../hooks/useSocialScreenshotUrl';
import BrowseBar from '../../components/storefront/browse/BrowseBar';
import ModulesSection from '../components/ModulesSection';
import ProviderSignupSection from '../components/ProviderSignupSection';
import StorefrontSearchBar from '../../components/storefront/search/StorefrontSearchBar';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import ThemesSection from '../components/ThemesSection';
import VideoSection from '../components/VideoSection';
import CalloutsSection from '../components/CalloutsSection';
const ASSETS_PUBLIC_LANGUAGES = ['en', 'de', 'es', 'fr', 'it', 'ja', 'nl', 'pt'];
const HEADER_CUSTOM_STYLE = {
  MAIN_SECTION_CLASSNAME: 'p-top-7',
  BACKGROUND_COLOR: '#850D63',
  GRADIENT_ANGLE: '223.23deg',
  START_GRADIENT_COLOR: '#850D63',
  END_GRADIENT_COLOR: '#5C62D6'
};
export default function TemplatesHomeContainer() {
  const {
    isEmbedded
  } = useEmbedContext();
  const lang = getLang();
  const canonicalUrl = getFullTemplatesPublicUrl(lang);
  const metaDescription = unescapedText('marketplace-assets-ui.templates.metaDescription');
  const [isSticky, setIsSticky] = useState(false);
  const onStickyChange = useCallback(evt => {
    setIsSticky(evt.target.value);
  }, []);
  const renderedHrefLangLinks = useMemo(() => {
    const hrefLangConfigs = [{
      href: getFullTemplatesPublicUrl('en'),
      isDefault: true,
      lang: 'en'
    }, ...ASSETS_PUBLIC_LANGUAGES.map(_lang => ({
      href: getFullTemplatesPublicUrl(_lang),
      isDefault: false,
      lang: _lang
    }))];
    return hrefLangConfigs.map(({
      href,
      isDefault,
      lang: _lang
    }) => /*#__PURE__*/_jsx(HrefLangLink, {
      href: href,
      lang: _lang,
      "x-default": isDefault
    }, `href-lang-${isDefault ? 'default' : _lang}`));
  }, []);
  const {
    trackInteraction,
    trackPageview,
    setDefaultEventProperties
  } = useContext(StorefrontUsageTrackerContext);
  useEffect(() => setDefaultEventProperties({
    screen: 'templates homepage'
  }), [setDefaultEventProperties]);
  useEffect(() => {
    trackPageview();
  }, [trackPageview]);
  useEffect(() => {
    document.title = I18n.text('marketplace-assets-ui.templates.htmlTitle');
  }, []);
  const socialScreenshotUrl = useSocialScreenshotUrl();
  return /*#__PURE__*/_jsxs(NavMarker, {
    name: "TEMPLATES_LOAD",
    children: [/*#__PURE__*/_jsx(PublicAppContext, {
      children: /*#__PURE__*/_jsx(DocumentHead, {
        children: /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(MetaDescription, {
            content: metaDescription
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "card"
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "site"
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "image",
            content: socialScreenshotUrl
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "type"
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "url",
            content: canonicalUrl
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "description",
            content: metaDescription
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "image",
            content: socialScreenshotUrl
          }), /*#__PURE__*/_jsx(CanonicalLink, {
            href: canonicalUrl
          }), renderedHrefLangLinks, /*#__PURE__*/_jsx(PrerenderReady, {})]
        })
      })
    }), /*#__PURE__*/_jsxs("main", {
      children: [/*#__PURE__*/_jsx(CoreStorefrontHeader, {
        collapsedTitle: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.templates.header.stickyTitle"
        }),
        compact: isEmbedded,
        customStyle: HEADER_CUSTOM_STYLE,
        subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.storefront.header.subtitle"
        }),
        title: /*#__PURE__*/_jsx(H1, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.storefront.header.title"
          })
        }),
        onStickyChange: onStickyChange,
        children: /*#__PURE__*/_jsx(StorefrontSearchBar, {
          isSticky: isSticky
        })
      }), /*#__PURE__*/_jsx(BrowseBar, {
        hasActiveOfferingType: false
      }), /*#__PURE__*/_jsx(MainSection, {
        children: /*#__PURE__*/_jsxs(MainSectionInner, {
          children: [/*#__PURE__*/_jsx(H2, {
            className: "is--heading-1 text-center p-top-5",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.templates.video.title"
            })
          }), /*#__PURE__*/_jsx(H3, {
            className: "is--heading-2 text-center m-bottom-10",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.templates.video.subtitle"
            })
          }), /*#__PURE__*/_jsx(VideoSection, {}), /*#__PURE__*/_jsx(CalloutsSection, {
            onTrackInteraction: trackInteraction
          })]
        })
      }), /*#__PURE__*/_jsx(MainSection, {
        color: "light",
        className: "p-x-0",
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsx(ThemesSection, {
            onTrackInteraction: trackInteraction
          })
        })
      }), /*#__PURE__*/_jsx(MainSection, {
        className: "p-x-0",
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsx(ModulesSection, {
            onTrackInteraction: trackInteraction
          })
        })
      }), /*#__PURE__*/_jsx(MainSection, {
        color: "light",
        className: "p-all-0",
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsx(ProviderSignupSection, {
            onTrackInteraction: trackInteraction
          })
        })
      })]
    })]
  });
}